import * as UI from "@iyk/ui"
import { HlsVideo } from "@iyk/vf-ui"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import * as React from "react"

import { ExclusiveCard } from "./exclusive-card.tsx"
import { useExclusiveSheet } from "./use-exclusive-sheet.ts"

const TIMER_DURATION = 2000

// #region Video Card

export const ExclusiveVideoCard = (props: ExclusiveVideoCard.Props) => {
  const [open, setOpen] = useExclusiveSheet(props.id)

  const pauseOtherMedia = () => {
    const audioElements = document.querySelectorAll("audio")
    audioElements.forEach((audio) => {
      if (!audio.paused) {
        audio.pause()
      }
    })
  }

  const onOpenChange = (open: boolean) => {
    setOpen(open)
    if (open) {
      pauseOtherMedia()
    }
  }

  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange}>
      <Sheet.Trigger>
        <ExclusiveCard.Container>
          <ExclusiveCard.CoverImage src={props.coverImage} alt={props.title} />
          <ExclusiveCard.IconButton>
            <UI.Icon.Play />
          </ExclusiveCard.IconButton>

          <ExclusiveCard.Heading>
            <ExclusiveCard.Title>{props.title}</ExclusiveCard.Title>
            <ExclusiveCard.Subtitle>{props.subtitle}</ExclusiveCard.Subtitle>
          </ExclusiveCard.Heading>
        </ExclusiveCard.Container>
      </Sheet.Trigger>
      <Sheet.Content title={props.title} description={props.subtitle}>
        <div className="h-dvh relative flex flex-col gap-4 justify-center items-center">
          <VideoPlayer
            id={props.id}
            title={props.title}
            coverImage={props.coverImage}
            videoSrc={props.videoSrc}
            onPlay={props.onPlay}
            subtitle={props.subtitle}
            autoPlay={props.autoPlay}
          />
          <UI.Button
            type="button"
            kind="shaded"
            size="md"
            className="absolute right-2 top-2"
            onClick={() => setOpen(false)}
          >
            <UI.Icon.Cross />
          </UI.Button>
        </div>
      </Sheet.Content>
    </Sheet.Root>
  )
}

namespace ExclusiveVideoCard {
  export type Props = {
    id: string
    title: string
    subtitle?: string
    coverImage: string
    videoSrc: string
    autoPlay?: boolean
    onPlay: () => void
  }
}

// #endregion

// #region Video Player

const VideoPlayer = ({ autoPlay = false, ...props }: ExclusiveVideoCard.Props) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [showButton, setShowButton] = React.useState(true)
  const hideButtonTimeout = React.useRef<NodeJS.Timeout | null>(null)

  const showPauseTemporarily = () => {
    setShowButton(true)
    if (hideButtonTimeout.current) clearTimeout(hideButtonTimeout.current)
    hideButtonTimeout.current = setTimeout(() => setShowButton(false), TIMER_DURATION)
  }

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play().catch(console.error)
      props.onPlay?.()
    } else {
      video.pause()
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    const video = videoRef.current
    if (!video) return

    if ((e.target as HTMLElement).closest(".play-pause-button")) return

    // Check if the click is within the visible area of the video
    const rect = video.getBoundingClientRect()
    const aspectRatioVideo = video.videoWidth / video.videoHeight
    const aspectRatioBox = rect.width / rect.height

    let contentWidth = rect.width
    let contentHeight = rect.height

    if (aspectRatioBox > aspectRatioVideo) {
      // there are black bars on the sides
      contentWidth = rect.height * aspectRatioVideo
    } else {
      // there are black bars on the top/bottom
      contentHeight = rect.width / aspectRatioVideo
    }

    const offsetX = (rect.width - contentWidth) / 2
    const offsetY = (rect.height - contentHeight) / 2

    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top

    const isInsideVisibleContent =
      clickX >= offsetX &&
      clickX <= rect.width - offsetX &&
      clickY >= offsetY &&
      clickY <= rect.height - offsetY

    if (!isInsideVisibleContent) return

    togglePlay()
  }

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const controller = new AbortController()

    const onPlay = () => {
      setIsPlaying(true)
      showPauseTemporarily()
    }

    const onPause = () => {
      setIsPlaying(false)
      setShowButton(true)
      if (hideButtonTimeout.current) clearTimeout(hideButtonTimeout.current)
    }

    const onEnded = () => {
      setIsPlaying(false)
      setShowButton(true)
    }

    video.addEventListener("play", onPlay, { signal: controller.signal })
    video.addEventListener("pause", onPause, { signal: controller.signal })
    video.addEventListener("ended", onEnded, { signal: controller.signal })

    return () => controller.abort()
  }, [])

  return (
    <div className="relative size-full">
      <HlsVideo
        ref={videoRef}
        onClick={handleClick}
        autoPlay={autoPlay}
        poster={props.coverImage}
        src={props.videoSrc}
        playsInline
        className="size-full object-contain"
      />
      <button
        onClick={togglePlay}
        className={UI.cx(
          "play-pause-button absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2",
          "flex items-center justify-center p-3 rounded-full bg-gray-5 text-white",
          "transition-opacity duration-300",
          showButton ? "opacity-100" : "opacity-0",
        )}
      >
        {isPlaying ? <UI.Icon.Pause /> : <UI.Icon.Play />}
      </button>
    </div>
  )
}

// #endregion

// #region Video Card Sheet

const SheetRoot = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = () => <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-transparent" />

const sheetContentClasses = UI.cx([
  "fixed max-w-[var(--app-max-width)] mx-auto z-50 gap-4 bg-background shadow-lg transition ease-in-out text-foreground",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:duration-500 data-[state=open]:duration-500",
  "inset-x-0 top-0",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
])

const SheetContent = ({ children, title, description, ...props }: Sheet.ContentProps) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      className={sheetContentClasses}
      onOpenAutoFocus={(e) => e.preventDefault()}
      {...props}
    >
      <div className="sr-only">
        <SheetPrimitive.Title>{title}</SheetPrimitive.Title>
        {description && <SheetPrimitive.Description>{description}</SheetPrimitive.Description>}
      </div>
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
)

namespace Sheet {
  export type ContentProps = {
    title: string
    description?: string
    children: React.ReactNode
  } & SheetPrimitive.DialogContentProps
}

const Sheet = {
  Root: SheetRoot,
  Content: SheetContent,
  Overlay: SheetOverlay,
  Portal: SheetPortal,
  Trigger: SheetTrigger,
}

// #endregion
